import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import {
  COVERAGE_CRITERIA_ALL,
  POLICY_STATUS_EDIT,
  POLICY_STATUS_NEW,
  POLICY_STATUS_VARIANCE,
  POLICY_STATUS_NEW_OR_VARIANCE,
  tabOrder,
  PXDX,
  HISTORY_TRANSLATION,
  DEPENDENCY,
  INCOMPATIBILITY,
  DEMOGRAPHICS,
  FAILED_VALIDATION,
  ALL,
} from "../PolicyTranslation/Constants/PolicyTranslationConstants";
import {
  filterRules,
  getAllRulesForValidation,
  getAvailableProcedureCodes,
  getCoverageCriteriaList,
  getTabLabels,
  getLobList,
} from "../PolicyTranslation/Utils/TranslationUtils";
import {
  initializeWorkspaceController,
  updatePolicyData,
} from "../../utils/WorkspaceUtils";
import TopPanelComponent from "./TopPanelComponent";
import { TranslationFilter } from "../PolicyTranslation/Filters/FiltersComponent";
import PolicyTabsComponent from "../PolicyTranslation/PolicyTabs/PolicyTabsComponent";
import emptyRowTemplate from "../PolicyTranslation/Constants/EmptyRowTemplate.json";
import { useWorkspace } from "context/WorkspaceContext";
import { Box } from "@mui/material";
import useGridResizeHook, {
  calculateGridHeight,
} from "hooks/useGridResizeHook";
import {
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Backdrop,
  CircularProgress,
} from "@mui/material";

import { Add } from "@mui/icons-material";
import api from "../../api";
import { setDialog } from "slices/appSlice";
import { formErrorStrfromObj } from "utils/shared";
import { WORKFLOW_CONFIGURATION } from "constants/Constants";
import {
  validatedDemographics,
  validatedDependency,
  validatedHistoryTranslation,
  validatedIncompatibility,
  validatePXDX,
} from "../PolicyTranslation/Validation/ValidationHelper";

const UseCases = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const topPanelRef = useRef(null);
  const [gridHeight, setGridHeight] = useState("auto");
  const policyState = location?.state?.policyState;

  const { policyNumber } = location.state;
  const { policyTitle } = location.state;

  const { workspaceController, setWorkspaceController } = useWorkspace();
  const [workspaceLoaded, setWorkspaceLoaded] = useState(false);
  const editable = policyState.editMode || false;
  const [policyRules, setPolicyRules] = useState(null);
  const [availableProcedureCodes, setAvailableProcedureCodes] = useState([]);
  const [selectedCC, setSelectedCC] = useState(COVERAGE_CRITERIA_ALL);
  const [selectedVariance, setSelectedVariance] = useState("All");
  const [selectedUseCase, setSelectedUseCase] = useState("All");
  const [newRowStructure] = useState(emptyRowTemplate);
  const [coverageCriteriaList, setCoverageCriteriaList] = useState([]);
  const [selectedvalidation, setselectedvalidation] = useState(ALL);
  const [selectedLob, setSelectedLob] = useState("All");
  const [lobList, setLobList] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [dynamicTabs, setDynamicTabs] = useState(getTabLabels());
  const [policyData, setPolicyData] = useState({
    configurationStatus: "",
    policyConfigurator: "",
    reviewType: "",
    overallAdoptionDecision: "",
    lobAdoptionDecisionList: [],
    policyChangeControlXrefDto: {
      policyChangeControlXrefKey: null,
      changeControlText: "",
    },
  });
  const [dxLimitationValues, setDxLimitationValues] = useState([]);
  const deletedRowId = useRef(null);

  const dynamicMenuItems = dynamicTabs.filter((t) => !t.display);

  useEffect(() => {
    initializeWorkspaceController(
      workspaceController,
      setWorkspaceController,
      setWorkspaceLoaded
    );
  }, [workspaceController, setWorkspaceController]);

  useEffect(() => {
    if (policyRules != null && tabValue === -1) {
      let tableId = null;
      for (let table in policyRules) {
        if (policyRules[table].length > 0) {
          tableId = table;
          break;
        }
      }
      setTabValue(tabOrder.get(tableId));
    }
  }, [policyRules, tabValue]);

  const getDxLimitationValues = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.PolicyRules.getDxLimitationList();
      if (
        response.status === 200 &&
        response.data !== null &&
        response.data.length > 0
      ) {
        setDxLimitationValues(response.data);
      } else if (response.data == null || response.data.length === 0) {
        throw new Error("No Dx Limitation Values found.");
      }
    } catch (e) {
      dispatch(
        setDialog({
          title: "Error",
          message: formErrorStrfromObj(e),
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (workspaceController != null) {
      setPolicyRules(
        filterRules(
          workspaceController.getClobPolicyRulesList(),
          selectedCC,
          selectedVariance,
          selectedUseCase,
          selectedLob,
          policyState?.healthPlanName,
          setDynamicTabs
        )
      );
      setCoverageCriteriaList(
        getCoverageCriteriaList(workspaceController.getClobPolicyRulesList())
      );
      setLobList(getLobList(workspaceController.getClobPolicyRulesList()));
    }
  }, [
    dispatch,
    policyState?.healthPlanName,
    selectedCC,
    selectedLob,
    selectedVariance,
    workspaceController,
    selectedUseCase,
  ]);

  useEffect(() => {
    getDxLimitationValues();
  }, [getDxLimitationValues]);

  useEffect(() => {
    if (workspaceController != null && availableProcedureCodes.length === 0) {
      setAvailableProcedureCodes(
        getAvailableProcedureCodes(workspaceController.getClobPolicyRulesList())
      );
    }
  }, [availableProcedureCodes, workspaceController]);

  const filterInvalidRules = (rows, validation) => {
    if (validation === FAILED_VALIDATION) {
      return rows.filter((row) => {
        return row.errors ? Object.keys(row.errors).length > 0 : false;
      });
    }
    return rows;
  };
  ("");

  const updateTabLength = (tabKey, newLength) => (prevTabs) => {
    const tabIndex = prevTabs.findIndex((tab) => tab.key === tabKey);
    if (tabIndex !== -1) {
      const updatedTabs = [...prevTabs];
      updatedTabs[tabIndex] = {
        ...updatedTabs[tabIndex],
        length: newLength,
      };
      return updatedTabs;
    }
    return prevTabs;
  };

  const pxdxRows = useMemo(() => {
    if (!policyRules || !policyRules[PXDX]) return [];
    const filteredRows = filterInvalidRules(
      policyRules[PXDX].map((row) =>
        validatePXDX(policyData?.lobAdoptionDecisionList || [], row)
      ),
      selectedvalidation
    );
    setDynamicTabs(updateTabLength(PXDX, filteredRows.length));
    return filteredRows;
  }, [
    policyRules?.[PXDX],
    selectedvalidation,
    policyData?.lobAdoptionDecisionList,
  ]);

  const historyRows = useMemo(() => {
    if (!policyRules || !policyRules[HISTORY_TRANSLATION]) return [];
    const filteredRows = filterInvalidRules(
      policyRules[HISTORY_TRANSLATION].map((row) =>
        validatedHistoryTranslation(
          policyData?.lobAdoptionDecisionList || [],
          row
        )
      ),
      selectedvalidation
    );
    setDynamicTabs(updateTabLength(HISTORY_TRANSLATION, filteredRows.length));
    return filteredRows;
  }, [
    policyRules?.[HISTORY_TRANSLATION],
    policyData?.lobAdoptionDecisionList,
    selectedvalidation,
  ]);

  const inCompabilityRows = useMemo(() => {
    if (!policyRules || !policyRules[INCOMPATIBILITY]) return [];
    const filteredRows = filterInvalidRules(
      policyRules[INCOMPATIBILITY].map((row) =>
        validatedIncompatibility(policyData?.lobAdoptionDecisionList || [], row)
      ),
      selectedvalidation
    );
    setDynamicTabs(updateTabLength(INCOMPATIBILITY, filteredRows.length));
    return filteredRows;
  }, [
    policyRules?.[INCOMPATIBILITY],
    policyData?.lobAdoptionDecisionList,
    selectedvalidation,
  ]);

  const demographicRows = useMemo(() => {
    if (!policyRules || !policyRules[DEMOGRAPHICS]) return [];
    const filteredRows = filterInvalidRules(
      policyRules[DEMOGRAPHICS].map((row) =>
        validatedDemographics(policyData?.lobAdoptionDecisionList || [], row)
      ),
      selectedvalidation
    );
    setDynamicTabs(updateTabLength(DEMOGRAPHICS, filteredRows.length));
    return filteredRows;
  }, [
    policyRules?.[DEMOGRAPHICS],
    policyData?.lobAdoptionDecisionList,
    selectedvalidation,
  ]);

  const dependencyRows = useMemo(() => {
    if (!policyRules || !policyRules[DEPENDENCY]) return [];
    const filteredRows = filterInvalidRules(
      policyRules[DEPENDENCY].map((row) =>
        validatedDependency(policyData?.lobAdoptionDecisionList || [], row)
      ),
      selectedvalidation
    );
    setDynamicTabs(updateTabLength(DEPENDENCY, filteredRows.length));
    return filteredRows;
  }, [
    policyRules?.[DEPENDENCY],
    policyData?.lobAdoptionDecisionList,
    selectedvalidation,
  ]);

  const addNewRows = (checkedItems, ruleFlowGroup) => {
    const healthPlanDefaultId = policyState?.healthPlanDefaultId || "";
    const newRows = checkedItems.map((item, index) => {
      let newRow = JSON.parse(JSON.stringify(newRowStructure));
      newRow.RuleFacts.ProcedureCode = item.code;
      newRow.RuleFacts.ProcedureCodeDescription = item.codeDescription;
      newRow.RuleFacts.CoverageCriteriaList = item.coverageCriteriaList;
      newRow.RuleFacts.RuleEndDate = "";
      newRow.RuleFacts.AllowedTimeframe = "";
      newRow.RuleFacts.AgeRange = "";
      newRow.RuleFacts.DXLimitation = "";
      newRow.RuleFacts.DiagnosisListExpanded = item.DiagnosisListExpanded;
      newRow.RuleFacts.CodingClassification = item.CodingClassification;
      newRow.RuleFacts.DiagnosisListCondensed = item.DiagnosisListCondensed;
      newRow.RuleHeader.PolicyNumber = item.PolicyNumber;
      newRow.RuleHeader.ChangeControl = item.ChangeControl;
      if (healthPlanDefaultId === "AHCS") {
        newRow.RuleHeader.EditRule = POLICY_STATUS_NEW;
      } else {
        newRow.RuleHeader.EditRule = POLICY_STATUS_VARIANCE;
      }
      newRow.RuleHeader.PolicyBusinessRuleName = "";
      newRow.RuleHeader.RuleFlowGroup = ruleFlowGroup;
      return newRow;
    });

    setWorkspaceController(
      workspaceController.cloneController().addBusinessRule(newRows)
    );
  };

  const duplicateRow = (id) => {
    const index = workspaceController.clob.PolicyBusinessRulesList.findIndex(
      (p) => p.index === id
    );
    const duplicateIndex = index + 1;
    setWorkspaceController(
      workspaceController
        .cloneController()
        .duplicateRule(index)
        .setEditRule(duplicateIndex, POLICY_STATUS_NEW_OR_VARIANCE)
        .incrementRuleVersion(duplicateIndex)
    );
  };

  const updateRulesAfterEdit = (newRow, oldRow) => {
    const { index } = newRow;
    let newController;
    if (
      [POLICY_STATUS_NEW, POLICY_STATUS_VARIANCE].includes(
        workspaceController.getEditRule(index)
      )
    ) {
      newController = workspaceController
        .cloneController()
        .setRule(index, newRow);
    } else {
      const duplicateIndex = index + 1;
      const editRule = workspaceController.getEditRule(index);
      newController = workspaceController
        .cloneController()
        .setEditRule(
          index,
          editRule === "VARIANCE" ? "VARIANCE" : POLICY_STATUS_EDIT
        )
        .duplicateRule(index)
        .setRule(duplicateIndex, { ...newRow, index: duplicateIndex })
        .setEditRule(duplicateIndex, POLICY_STATUS_NEW_OR_VARIANCE)
        .incrementRuleVersion(duplicateIndex)
        .setPolicyBusinessRuleName(duplicateIndex, "")
        .setRuleEffectiveDate(duplicateIndex, "");
    }
    setWorkspaceController(newController);

    return newRow;
  };

  const handleCancelAction = () => {
    setWorkspaceController(null);
    navigate(-1);
  };

  const resizeEvent = useCallback(
    () => calculateGridHeight(topPanelRef, setGridHeight, 40),
    [topPanelRef, setGridHeight]
  );
  useGridResizeHook(resizeEvent);
  useEffect(() => {
    if (topPanelRef.current) {
      resizeEvent(); // Only fires once when .current is first set
    }
  }, [topPanelRef.current]);
  const handleAccordionToggle = () => {
    setTimeout(() => {
      resizeEvent();
    }, 400);
  };

  const openPlusTab = (event) => {
    setPlusTabAnchor(event.currentTarget);
  };

  const handleTabChange = (event, newValue) => {
    setLoading(true);
    setTimeout(() => {
      setTabValue(newValue);
    }, 100);
  };

  const [plusTabAnchor, setPlusTabAnchor] = useState("");

  const [loading, setLoading] = useState(true);

  const addTab = (tabKey) => {
    let tabIndex = tabOrder.get(tabKey);
    const dynamicTabsIndex = dynamicTabs.findIndex((t) => t.key === tabKey);
    setDynamicTabs(
      dynamicTabs.toSpliced(dynamicTabsIndex, 1, {
        key: tabKey,
        length: 0,
        display: true,
      })
    );
    setTabValue(tabIndex);
    closePlusTab();
  };

  const closePlusTab = () => {
    setPlusTabAnchor("");
  };

  const handleValidate = (submit) => {
    const allRules = getAllRulesForValidation(
      workspaceController.clob.PolicyBusinessRulesList
    );

    if (submit) {
      const activeRules = Object.values(allRules).flat();

      //check to see if any of the rules useCases is not set to complete
      const incompleteUseCases = activeRules.filter(
        (rule) =>
          rule.RuleHeader?.UseCase === undefined ||
          rule.RuleHeader?.UseCase !== "Complete"
      );

      if (incompleteUseCases.length > 0) {
        dispatch(
          setDialog({
            title: "Error",
            message: "Please complete all Use Cases before submitting.",
          })
        );
        return false; // validation failed
      }
    }

    return true; // validation passed
  };

  if (!workspaceLoaded || !workspaceController || !policyRules) {
    return <div>Loading...</div>;
  }

  const handleRemoveInvalidRow = (index) => {
    const newController = workspaceController
      .cloneController()
      .removeRule(index);
    setWorkspaceController(newController);
  };

  /**
   * Adds the LobConfigKey to the existing row so that the end dates can be set.
   *
   * @param {Object} oldRow - The row object to update with the LobConfigKey.
   */
  const endExistingRule = (oldRow) => {
    const index = workspaceController.clob.PolicyBusinessRulesList.findIndex(
      (p) => p.index === oldRow.index
    );

    const endDate = oldRow.RuleFacts.RuleEndDate || "";

    const newController = workspaceController
      .cloneController()
      .setEditRule(index, POLICY_STATUS_EDIT)
      .setRule(index, oldRow);
    setWorkspaceController(newController);
  };

  return (
    <Box>
      <Box ref={topPanelRef} className="top-half">
        <TopPanelComponent
          updatePolicyData={(submit, updateAssignessReviewerDto) => {
            // If validation fails, short-circuit (return immediately).
            if (!handleValidate(submit)) {
              return;
            }
            updatePolicyData(
              dispatch,
              navigate,
              policyState,
              submit,
              workspaceController,
              null,
              null,
              updateAssignessReviewerDto,
              null,
              WORKFLOW_CONFIGURATION
            );
          }}
          policyStateData={policyState}
          deletedRowId={deletedRowId}
          healthPlanDisplayName={policyState?.healthPlanDisplayName || ""}
          policyNumber={policyNumber}
          policyTitle={policyTitle}
          editable={editable}
          healthPlanKey={workspaceController?.workspace?.healthPlanKey || "-1"}
          policyKey={policyState?.policyKey}
          workspaceController={workspaceController}
          handleCancelAction={handleCancelAction}
          sx={{ mb: 4 }}
          policyVersion={location?.state?.policyVersion || ""}
          policyData={policyData}
          setPolicyData={setPolicyData}
        >
          <TranslationFilter
            ccList={coverageCriteriaList}
            lobList={lobList}
            selectedLob={selectedLob}
            setSelectedLob={setSelectedLob}
            setSelectedCC={setSelectedCC}
            selectedCC={selectedCC}
            selectedVariance={selectedVariance}
            setSelectedVariance={setSelectedVariance}
            selectedUseCase={selectedUseCase}
            setSelectedUseCase={setSelectedUseCase}
            onAccordionToggle={handleAccordionToggle}
            showVarianceFilter={false}
            showUseCaseFilter={true}
            policyData={policyData}
          />

          <Box
            sx={{
              display: "flex",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& button": {
                  textTransform: "none",
                },
              }}
            >
              {dynamicTabs.map(
                (tab) =>
                  tab.display && (
                    <Tab
                      key={tab.key}
                      value={tabOrder.get(tab.key)}
                      label={`${tab.key} (${tab.length})`}
                    />
                  )
              )}
            </Tabs>
            {dynamicMenuItems.length !== 0 && (
              <IconButton onClick={openPlusTab}>
                <Add fontSize="small" />
              </IconButton>
            )}
            <Menu
              anchorEl={plusTabAnchor}
              onClose={closePlusTab}
              open={Boolean(plusTabAnchor)}
            >
              {dynamicMenuItems.map(({ key }) => {
                return (
                  <MenuItem key={key} onClick={() => addTab(key)}>
                    {key}
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </TopPanelComponent>
      </Box>
      <Box>
        {tabValue !== -1 && (
          <PolicyTabsComponent
            setLoading={setLoading}
            gridHeight={gridHeight}
            policyRules={policyRules}
            tabValue={tabValue}
            setTabValue={setTabValue}
            handleDelete={{}} //Passing an empty function because this will not be used from UseCases.
            dynamicTabs={dynamicTabs}
            setDynamicTabs={setDynamicTabs}
            selectedCC={selectedCC}
            selectedVariance={selectedVariance}
            setSelectedVariance={setSelectedVariance}
            editable={editable}
            setSelectedCC={setSelectedCC}
            chips={coverageCriteriaList}
            availableProcedureCodes={availableProcedureCodes}
            newRowStructure={newRowStructure}
            addNewRows={addNewRows}
            duplicateRow={duplicateRow}
            updateRulesAfterEdit={updateRulesAfterEdit}
            policyData={policyData}
            dxLimitationValues={dxLimitationValues}
            pageComponent="UseCases"
            handleRemoveInvalidRow={handleRemoveInvalidRow}
            endExistingRule={endExistingRule}
            pxdxRows={pxdxRows}
            historyRows={historyRows}
            inCompabilityRows={inCompabilityRows}
            demographicRows={demographicRows}
            dependencyRows={dependencyRows}
          />
        )}
      </Box>
    </Box>
  );
};
export default UseCases;
